import * as Sentry from "@sentry/react";
import { useState } from "react";
import { useConversionParams } from "/context/ConversionParamsProvider.jsx";
import { getFrontendUrl } from "/utils/url.helper.js";
import useAuthRedirect from "./useAuthRedirect";

const ssoRegisterHandler = async (provider, formValues) => {
  try {
    const baseUrl = import.meta.env.VITE_REACT_APP_API_BASE_URL;

    if (!baseUrl) {
      throw new Error(
        "API base URL not configured. Please set VITE_REACT_APP_API_BASE_URL in environment variables."
      );
    }

    // Step 1: Get authorization link from backend
    const payload = {
      ...formValues,
      redirect_url: getFrontendUrl(`/auth/${provider}/callback`, {
        context: "registration",
      }),
    };

    const linkResponse = await fetch(
      `${baseUrl}auth/socials/${provider}/register/link`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      }
    );

    if (!linkResponse.ok) {
      throw new Error(
        `Failed to get ${provider} authorization link: ${linkResponse.status}`
      );
    }

    const linkData = await linkResponse.json();

    if (!linkData.url) {
      throw new Error(`Invalid response from ${provider} link endpoint`);
    }

    window.location.href = linkData.url;
    return;
  } catch (error) {
    throw new Error(`Failed to get ${provider} authorization link: ${error}`);
  }
};

const ssoLoginHandler = async (provider) => {
  try {
    const baseUrl = import.meta.env.VITE_REACT_APP_API_BASE_URL;

    if (!baseUrl) {
      throw new Error(
        "API base URL not configured. Please set VITE_REACT_APP_API_BASE_URL in environment variables."
      );
    }

    // Step 1: Get authorization link from backend
    const payload = {
      redirect_url: getFrontendUrl(`/auth/${provider}/callback`, {
        context: "login",
      }),
    };
    const query = payload ? `?${new URLSearchParams(payload).toString()}` : "";
    const linkResponse = await fetch(
      // TODO: Link needs to be updated.
      `${baseUrl}auth/socials/${provider}/login/link${query}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        cache: "no-store",
      }
    );

    if (!linkResponse.ok) {
      throw new Error(
        `Failed to get ${provider} authorization link: ${linkResponse.status}`
      );
    }

    const linkData = await linkResponse.json();

    if (!linkData.url) {
      throw new Error(`Invalid response from ${provider} link endpoint`);
    }

    window.location.href = linkData.url;
    return;
  } catch (error) {
    throw new Error(`Failed to get ${provider} authorization link: ${error}`);
  }
};

export const useSSO = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const conversionParams = useConversionParams();
  const [ref_url] = useState(document.referrer || location.href);
  const { wipePathCookie, storeCurrentPath } = useAuthRedirect();

  const signUp = async (provider, formValues) => {
    setLoading(true);
    setError(null);

    const payload = {
      btag: conversionParams.btag,
      referral: conversionParams.r,
      referral_url: ref_url,
      ...formValues,
    };

    try {
      storeCurrentPath();
      await ssoRegisterHandler(provider, payload);
    } catch (err) {
      Sentry.captureException("Failed SSO Sign Up.", err);
      wipePathCookie();
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (provider) => {
    setLoading(true);
    setError(null);

    try {
      storeCurrentPath();
      await ssoLoginHandler(provider);
    } catch (err) {
      Sentry.captureException("Failed SSO Sign In.", err);
      wipePathCookie();
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return {
    error,
    loading,
    signIn,
    signUp,
  };
};
